// "Who can watch" (DS ShareDialog's VisibilityPicker section) for ShareDialog. `useShareSettings`
// owns the share state it reads (visibility §4 #21, grants #23-25, the public link #26);
// `WhoCanWatchSection` renders the picker plus the People / Team follow-ups.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Badge, Button, FilterChip, Icon, Input, VisibilityPicker, type VisibilityPickerProps } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { GrackleDetail, GrackleVisibility, ShareState, ShareSubjectType, TeamOut } from "../api/types";
import { visibilityIcon, visibilityLabel } from "../library/format";

interface PatchGrackleRequest {
  title?: string;
  description?: string;
  visibility?: GrackleVisibility;
}
interface CreateGrantRequest {
  subject_type: ShareSubjectType;
  user_id?: string;
  team_id?: string;
  email?: string;
}

type PickerValue = NonNullable<VisibilityPickerProps["value"]>;

/** The API's "specific" is the DS picker's "people"; the other four names match. */
const TO_PICKER: Record<GrackleVisibility, PickerValue> = {
  private: "private",
  specific: "people",
  team: "team",
  org: "org",
  public: "public",
};
const FROM_PICKER: Record<string, GrackleVisibility> = {
  private: "private",
  people: "specific",
  team: "team",
  org: "org",
  public: "public",
};

/** The VisibilityPicker's own label and icon for each level, for controls that name the current one. */
export const VISIBILITY_LEVEL: Record<GrackleVisibility, { label: string; icon: string }> = Object.fromEntries(
  (["private", "specific", "team", "org", "public"] as const).map((v) => [
    v,
    { label: visibilityLabel(v), icon: visibilityIcon(v) },
  ]),
) as Record<GrackleVisibility, { label: string; icon: string }>;

/** The one plain sentence that follows the picker (VisibilityPicker.prompt.md). */
export const FOOTNOTE: Record<GrackleVisibility, string> = {
  private: "Only you can watch. Nobody else has a link.",
  specific: "Only the people you add can watch. Everyone else gets a not-found page.",
  team: "Members of the chosen teams can watch. Nobody outside them can.",
  org: "Everyone signed in at this organization who has the link can watch.",
  public: "Anyone with the link can watch. No sign-in needed.",
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/; // §2.10

export function useShareSettings(grackleId: string) {
  const [detail, setDetail] = useState<GrackleDetail | null>(null);
  const [state, setState] = useState<ShareState | null>(null);
  const [teams, setTeams] = useState<TeamOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadShare = useCallback(async () => {
    const s = await api<ShareState>("GET", `/grackles/${grackleId}/share`);
    setState(s);
    return s;
  }, [grackleId]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([api<GrackleDetail>("GET", `/grackles/${grackleId}`), loadShare()])
      .then(([d]) => {
        if (cancelled) return;
        setDetail(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiFail ? err.message : "Couldn't load sharing settings.");
      });
    return () => {
      cancelled = true;
    };
  }, [grackleId, loadShare]);

  // Team visibility needs the org's team list for the picker — fetched lazily, only once we
  // know org_id (from GrackleDetail) and only when it's actually relevant.
  useEffect(() => {
    if (!detail || teams !== null) return;
    if (state?.visibility !== "team") return;
    api<{ items: TeamOut[] }>("GET", `/orgs/${detail.org_id}/teams`)
      .then((res) => setTeams(res.items))
      .catch(() => setTeams([]));
  }, [detail, state?.visibility, teams]);

  async function run(action: () => Promise<unknown>, failure: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : failure);
    } finally {
      setBusy(false);
    }
  }

  function chooseVisibility(picked: string) {
    const next = FROM_PICKER[picked];
    if (!next || !state || state.visibility === next) return;
    void run(async () => {
      await api<GrackleDetail>("PATCH", `/grackles/${grackleId}`, { visibility: next } satisfies PatchGrackleRequest);
      // Issue #9: grants that no longer apply are cleared server-side; re-fetch for clean state.
      await loadShare();
      if (next === "team" && teams === null && detail) {
        const res = await api<{ items: TeamOut[] }>("GET", `/orgs/${detail.org_id}/teams`);
        setTeams(res.items);
      }
    }, "Couldn't update visibility.");
  }

  function addEmailGrant(email: string, onAdded: () => void) {
    void run(async () => {
      await api<ShareState>("POST", `/grackles/${grackleId}/grants`, {
        subject_type: "email",
        email,
      } satisfies CreateGrantRequest);
      await loadShare();
      onAdded();
    }, "Couldn't add that person.");
  }

  function removeGrant(grantId: string) {
    void run(async () => {
      await api<ShareState>("DELETE", `/grackles/${grackleId}/grants/${grantId}`);
      await loadShare();
    }, "Couldn't remove that.");
  }

  function toggleTeamGrant(team: TeamOut) {
    if (!state) return;
    const existing = state.grants.find((g) => g.subject_type === "team" && g.team?.id === team.id);
    void run(async () => {
      if (existing) {
        await api<ShareState>("DELETE", `/grackles/${grackleId}/grants/${existing.id}`);
      } else {
        await api<ShareState>("POST", `/grackles/${grackleId}/grants`, {
          subject_type: "team",
          team_id: team.id,
        } satisfies CreateGrantRequest);
      }
      await loadShare();
    }, "Couldn't update that team.");
  }

  return {
    detail,
    state,
    teams,
    error,
    busy,
    loadShare,
    run,
    chooseVisibility,
    addEmailGrant,
    removeGrant,
    toggleTeamGrant,
  };
}

export type ShareSettings = ReturnType<typeof useShareSettings>;

/** The "Who can watch" label and picker, then the People or Team follow-up for those levels. */
export function WhoCanWatchSection({ settings }: { settings: ShareSettings }) {
  const { state, teams, error, busy } = settings;
  const [emailInput, setEmailInput] = useState("");
  const visibility = state?.visibility;
  const peopleGrants = state?.grants.filter((g) => g.subject_type === "user" || g.subject_type === "email") ?? [];

  function addEmail(e: FormEvent) {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return;
    settings.addEmailGrant(email, () => setEmailInput(""));
  }

  return (
    <>
      <div className="share__section">
        <div className="share__label">Who can watch</div>
        {visibility ? (
          <VisibilityPicker value={TO_PICKER[visibility]} onChange={settings.chooseVisibility} />
        ) : (
          <span className="gk-caption">{error ? "Sharing settings didn't load." : "Loading…"}</span>
        )}
      </div>

      {visibility === "specific" ? (
        <div className="share__section">
          <div className="share__chips">
            {peopleGrants.length === 0 ? (
              <span className="gk-caption">Nobody added yet</span>
            ) : (
              peopleGrants.map((g) => (
                <Badge key={g.id} tone="neutral" size="lg" className="share__chip">
                  {g.user ? g.user.name : g.email}
                  <button
                    type="button"
                    className="share__chip-remove"
                    onClick={() => settings.removeGrant(g.id)}
                    aria-label={`Remove ${g.user ? g.user.name : g.email}`}
                    disabled={busy}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </Badge>
              ))
            )}
          </div>
          <form className="share__add" onSubmit={addEmail}>
            <Input
              type="email"
              icon="envelope-simple"
              placeholder="Add people by email"
              aria-label="Add people by email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              disabled={busy}
              wrapperStyle={{ flex: 1 }}
            />
            <Button type="submit" variant="secondary" disabled={busy || !EMAIL_RE.test(emailInput.trim())}>
              Add
            </Button>
          </form>
        </div>
      ) : null}

      {visibility === "team" ? (
        <div className="share__section">
          {teams === null ? (
            <span className="gk-caption">Loading teams…</span>
          ) : teams.length === 0 ? (
            <span className="gk-caption">This organization has no teams yet.</span>
          ) : (
            <div className="share__chips" role="group" aria-label="Teams">
              {teams.map((team) => {
                const on = state?.grants.some((g) => g.subject_type === "team" && g.team?.id === team.id) ?? false;
                return (
                  <FilterChip
                    key={team.id}
                    active={on}
                    icon={on ? "check" : "plus"}
                    aria-pressed={on}
                    onClick={() => settings.toggleTeamGrant(team)}
                    disabled={busy}
                  >
                    {team.name}
                  </FilterChip>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
